"use client";

import { IconHomeLine, IconMoonStar, IconSun } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useColorMode } from "@chakra-ui/react";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function NotFound() {
  const [glitchText, setGlitchText] = useState("404");
  const [scanlineOffset, setScanlineOffset] = useState(0);
  const { colorMode, toggleColorMode } = useColorMode();

  // Client-only animation intervals
  useEffect(() => {
    const glitchInterval = setInterval(() => {
      const glitchChars = ["4̸0̸4̸", "4̷0̷4̷", "4̶0̶4̶", "404", "4Ø4", "4O4"];
      setGlitchText(glitchChars[Math.floor(Math.random() * glitchChars.length)]);
      setTimeout(() => setGlitchText("404"), 100);
    }, 3000);

    const scanlineInterval = setInterval(() => {
      setScanlineOffset(prev => (prev + 1) % 100);
    }, 50);

    return () => {
      clearInterval(glitchInterval);
      clearInterval(scanlineInterval);
    };
  }, []);

  const isDark = colorMode === "dark";

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen w-screen flex-col overflow-hidden">
      {/* Menu bar */}
      <div className="border-ds-border-default bg-ds-surface-card relative z-[200] flex h-12 items-center justify-between border-b px-5">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2">
            <img src="https://cdn.b3.fun/b3os-logo-embossed.png" alt="B3" className="h-5 dark:invert" />
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <div className="label-xs flex items-center gap-2">
            <span className="font-mono">PAGE NOT FOUND</span>
          </div>
          <button
            onClick={toggleColorMode}
            className="text-ds-text-muted hover:bg-ds-surface-hover hover:text-ds-text-primary dark:hover:bg-ds-neutral-800 flex h-8 w-8 items-center justify-center rounded-md transition-colors dark:hover:text-zinc-100"
            aria-label="Toggle theme"
          >
            {isDark ? <IconSun className="h-4 w-4" /> : <IconMoonStar className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {/* Desktop background with dot grid */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden p-4 md:p-8"
        style={{
          background: isDark ? "#0a0a0a" : "#f5f5f5",
          backgroundImage: isDark
            ? "radial-gradient(circle, #27272a 1px, transparent 1px)"
            : "radial-gradient(circle, #d4d4d4 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      >
        {/* OS Window */}
        <div
          className={cn(
            "relative w-full max-w-2xl overflow-hidden rounded-xl shadow-2xl",
            "transition-all duration-500 ease-out",
            "animate-in fade-in-0 zoom-in-95",
          )}
          style={{
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)",
          }}
        >
          {/* Window title bar */}
          <div className="bg-ds-neutral-800 flex h-10 items-center justify-between px-4">
            {/* Traffic lights */}
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500 shadow-inner" />
              <div className="h-3 w-3 rounded-full bg-yellow-500 shadow-inner" />
              <div className="h-3 w-3 rounded-full bg-green-500 shadow-inner" />
            </div>
            {/* Window title */}
            <div className="text-ds-text-disabled absolute left-1/2 -translate-x-1/2 font-mono text-xs">
              b3os_not_found.exe
            </div>
            <div />
          </div>

          {/* BSOD Content */}
          {/* Intentional Windows BSOD blue — decorative 404 theme, not part of ds palette */}
          {/* eslint-disable-next-line local/no-arbitrary-tailwind-tokens */}
          <div className="relative overflow-hidden bg-[#0078D7] p-8 md:p-12" style={{ minHeight: "400px" }}>
            {/* Scanlines overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-10"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 2px,
                  rgba(0, 0, 0, 0.3) 2px,
                  rgba(0, 0, 0, 0.3) 4px
                )`,
                backgroundPosition: `0 ${scanlineOffset}px`,
              }}
            />

            {/* Subtle noise texture */}
            <div
              className="pointer-events-none absolute inset-0 opacity-5"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-start text-white">
              {/* Sad face */}
              <div className="mb-6 text-7xl font-light md:text-8xl">:(</div>

              {/* Main text */}
              <div className="mb-8">
                <p className="text-xl font-light leading-relaxed md:text-2xl">
                  The page you&apos;re looking for doesn&apos;t exist or has been moved. Let&apos;s get you back on
                  track.
                </p>
              </div>

              {/* Details box */}
              <div className="mb-8 w-full rounded border border-white/20 bg-white/10 p-4 font-mono text-sm backdrop-blur-sm">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <span className="text-white/60">Status:</span>
                    <span className="font-bold tracking-wider">{glitchText}</span>
                    <span className="text-white/60">NOT_FOUND</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white/60">Route:</span>
                    <span>unresolved</span>
                  </div>
                </div>
              </div>

              {/* Action button */}
              <Link href="/">
                <Button variant="white">
                  <div className="flex items-center gap-2 px-3 text-xs">
                    <IconHomeLine className="h-3 w-3" />
                    Go Home
                  </div>
                </Button>
              </Link>
            </div>

            {/* Glitch effect overlay - occasional */}
            <div
              className="pointer-events-none absolute inset-0 opacity-0"
              style={{
                animation: "glitch 5s infinite",
                background: "linear-gradient(90deg, transparent, rgba(255,0,0,0.03), transparent)",
              }}
            />
          </div>
        </div>

        {/* Decorative elements */}
        <div className="text-ds-text-muted dark:text-ds-text-subtle pointer-events-none absolute bottom-4 left-4 font-mono text-xs">
          B3OS v4.0.4
        </div>
      </div>
      {/* Keyframe animations */}
      <style jsx>{`
        @keyframes glitch {
          0%,
          95%,
          100% {
            opacity: 0;
            transform: translateX(0);
          }
          96% {
            opacity: 1;
            transform: translateX(-2px);
          }
          97% {
            opacity: 1;
            transform: translateX(2px);
          }
          98% {
            opacity: 1;
            transform: translateX(-1px);
          }
          99% {
            opacity: 0;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
